import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/api/client'
import { endpoints } from '@/api/endpoints'

interface Template {
  id: string
  name: string
  subject: string
  html: string
  version: number
  status: 'draft' | 'published'
  created_at: string
  updated_at: string
}

interface TemplateListResponse {
  data: Template[]
  total: number
  page: number
  per_page: number
}

interface CreateTemplateRequest {
  name: string
  subject: string
  html: string
}

interface UpdateTemplateRequest {
  name?: string
  subject?: string
  html?: string
}

const QUERY_KEY = ['templates'] as const

export function useTemplates(params?: {
  page?: number
  per_page?: number
  search?: string
}): ReturnType<typeof useQuery<TemplateListResponse>> {
  return useQuery({
    queryKey: [...QUERY_KEY, params],
    queryFn: () => apiClient.get<TemplateListResponse>(endpoints.templates.list(params)),
  })
}

export function useTemplate(id: string): ReturnType<typeof useQuery<Template>> {
  return useQuery({
    queryKey: [...QUERY_KEY, id],
    queryFn: () => apiClient.get<Template>(endpoints.templates.get(id)),
    enabled: !!id,
  })
}

export function useCreateTemplate(): ReturnType<
  typeof useMutation<Template, unknown, CreateTemplateRequest>
> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: CreateTemplateRequest) =>
      apiClient.post<Template>(endpoints.templates.create, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_KEY }),
  })
}

export function useUpdateTemplate(): ReturnType<
  typeof useMutation<Template, unknown, { id: string; data: UpdateTemplateRequest }>
> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateTemplateRequest }) =>
      apiClient.put<Template>(endpoints.templates.update(id), data),
    onSuccess: (_result, { id }) => {
      qc.invalidateQueries({ queryKey: QUERY_KEY })
      qc.invalidateQueries({ queryKey: [...QUERY_KEY, id] })
    },
  })
}

export function usePublishTemplate(): ReturnType<typeof useMutation<Template, unknown, string>> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiClient.post<Template>(endpoints.templates.publish(id), {}),
    onSuccess: (_result, id) => {
      qc.invalidateQueries({ queryKey: QUERY_KEY })
      qc.invalidateQueries({ queryKey: [...QUERY_KEY, id] })
    },
  })
}

export type { CreateTemplateRequest, Template, TemplateListResponse, UpdateTemplateRequest }
